import { useSubscription } from '@apollo/client';
import { useEffect, useState } from 'react';
import useIsPublicView from './useIsPublicView';
import { SessionActions } from '../account/sessionSlice';
import { useIsNonVisitorUser } from '../account/useIsLoggedIn';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import { gql } from '../graphql/generated';
import { UiThemeSetting } from '../graphql/generated/graphql';

const MATCH = '(prefers-color-scheme: dark)';

const UI_THEME_SUBSCRIPTION = gql(/** @complexity 4 */ `
  subscription UI_THEME_SUBSCRIPTION {
    userSettingsUpdated {
      uiTheme
    }
  }
`);

const getIsSystemDarkMode = () => !!window?.matchMedia(MATCH).matches;

const useUserUiTheme = () => {
  const isNonVisitorUser = useIsNonVisitorUser();
  const isPublicView = useIsPublicView();

  const dispatch = useAppDispatch();
  const theme = useAppSelector((state) => state.session.user.settings?.uiTheme);

  useSubscription(UI_THEME_SUBSCRIPTION, {
    skip: !isNonVisitorUser || isPublicView,
    onData: ({ data: { data } }) => {
      const nextTheme = data?.userSettingsUpdated.uiTheme;
      if (nextTheme !== theme) {
        dispatch(
          SessionActions.setUserSetting({
            uiTheme: nextTheme,
          }),
        );
      }
    },
  });

  const defaultTheme = isNonVisitorUser ? UiThemeSetting.Auto : null;

  return theme ?? defaultTheme;
};

export const useIsSystemDarkMode = () => {
  const [isSystemDarkMode, setIsSystemDarkMode] = useState(() =>
    getIsSystemDarkMode(),
  );

  useEffect(() => {
    const colorSchemeListener = () => {
      setIsSystemDarkMode(getIsSystemDarkMode());
    };

    const mediaQueryList = window?.matchMedia(MATCH);
    if (!mediaQueryList) return undefined;

    mediaQueryList.addEventListener('change', colorSchemeListener);
    return () => {
      mediaQueryList.removeEventListener('change', colorSchemeListener);
    };
  }, []);

  return isSystemDarkMode;
};

const useIsDarkMode = (): boolean => {
  const userTheme = useUserUiTheme();
  const isSystemDarkMode = useIsSystemDarkMode();

  return !userTheme || userTheme === UiThemeSetting.Auto
    ? isSystemDarkMode
    : userTheme === UiThemeSetting.Dark;
};

export default useIsDarkMode;
